import { useEffect, useRef, useState } from "react";
import { SvgIcon } from "./ui/SvgIcon";
import closeIcon from "../assets/close-icon.svg?raw";
import { getThemeColors } from "../lib/theme";

function addHiddenSection(current: string[], title: string): string[] {
  if (current.some((t) => t.toLowerCase() === title.toLowerCase())) {
    return current;
  }
  return [...current, title];
}

const COMMON_HIDDEN_SECTIONS: Record<string, string[]> = {
  en: ["See also"],
  de: ["Siehe auch"],
  es: ["Véase también"],
  fr: ["Voir aussi"],
  sl: ["Glej tudi"],
};

export function HiddenSectionsControls({
  hiddenSections = [],
  lang,
  isHost,
  isDark,
  t,
  send,
  setHiddenSections,
}: {
  hiddenSections: string[];
  lang: string;
  isHost: boolean;
  isDark: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  send: (action: unknown) => void;
  setHiddenSections: (titles: string[]) => unknown;
}) {
  const theme = getThemeColors(isDark);
  const commonSections =
    COMMON_HIDDEN_SECTIONS[lang] ?? COMMON_HIDDEN_SECTIONS.en;
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (customMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [customMode]);

  const isEnabled = (title: string) =>
    hiddenSections.some((t) => t.toLowerCase() === title.toLowerCase());

  const togglePreset = (title: string) => {
    if (!isHost) return;
    const next = isEnabled(title)
      ? hiddenSections.filter((t) => t.toLowerCase() !== title.toLowerCase())
      : addHiddenSection(hiddenSections, title);
    send(setHiddenSections(next));
  };

  const removeSection = (title: string) => {
    if (!isHost) return;
    send(
      setHiddenSections(
        hiddenSections.filter((t) => t.toLowerCase() !== title.toLowerCase()),
      ),
    );
  };

  const addCustom = () => {
    const title = customValue.trim();
    if (!title || !isHost) return;
    send(setHiddenSections(addHiddenSection(hiddenSections, title)));
    setCustomValue("");
  };

  const customSections = hiddenSections.filter(
    (t) => !commonSections.some((c) => c.toLowerCase() === t.toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-wrap items-center gap-2">
        {commonSections.map((title) => (
          <button
            key={title}
            data-testid={`hidden-section-preset-${title}`}
            aria-pressed={isEnabled(title)}
            onClick={() => togglePreset(title)}
            disabled={!isHost}
            className={isEnabled(title) ? theme.chipActive : theme.chip}
          >
            {title}
          </button>
        ))}

        {customSections.map((title) => (
          <span
            key={title}
            className="flex items-center gap-1.5 rounded-full border-2 border-ink bg-gold px-3 py-1 font-display text-xs text-ink shadow-board-sm"
          >
            {title}
            <button
              aria-label={t("lobby.settings.hiddenSectionsRemove", { title })}
              data-testid={`hidden-section-remove-${title}`}
              onClick={() => removeSection(title)}
              disabled={!isHost}
              className={`text-ink/60 hover:text-ink ${!isHost ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              <SvgIcon markup={closeIcon} size={12} />
            </button>
          </span>
        ))}

        <div className="sm:ml-auto">
          {customMode ? (
            <>
              <input
                ref={inputRef}
                data-testid="hidden-section-custom-input"
                type="text"
                value={customValue}
                disabled={!isHost}
                onChange={(e) => setCustomValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addCustom();
                }}
                onBlur={() => setCustomMode(false)}
                placeholder={t("lobby.settings.hiddenSectionsPlaceholder")}
                aria-label={t("lobby.settings.hiddenSectionsPlaceholder")}
                className={`px-3 py-1 text-xs outline-none ${theme.field} ${theme.inputPlaceholder} ${!isHost ? "opacity-50 cursor-not-allowed" : ""}`}
              />
              <button
                data-testid="hidden-section-custom-add-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={addCustom}
                disabled={!isHost || !customValue.trim()}
                className={`ml-1.5 ${theme.chipActive}`}
              >
                {t("lobby.settings.hiddenSectionsAdd")}
              </button>
            </>
          ) : (
            <button
              data-testid="hidden-section-custom-btn"
              onClick={() => isHost && setCustomMode(true)}
              disabled={!isHost}
              className={theme.chip}
            >
              {t("lobby.settings.customDuration")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
